/*
 * @nevware21/ts-utils
 * https://github.com/nevware21/ts-utils
 *
 * Copyright (c) 2022 NevWare21 Solutions LLC
 * Licensed under the MIT license.
 */

import { getKnownSymbol } from "../symbol/symbol";
import { WellKnownSymbols } from "../symbol/well_known";
import { isFunction, isStrictNullOrUndefined } from "../helpers/base";

/**
 * Checks if the type of value looks like an iterator instance (contains a next function).
 *
 * @since 0.4.0
 * @group Type Identity
 * @group Iterator
 * @typeParam T - Identifies the return type of the iterator defaults to any
 * @param value - The value to be checked
 * @returns True if the value is an Iterator, otherwise false
 * @example
 * ```ts
 * isIterator(null);        // false
 * isIterator(undefined);   // false
 * isIterator("null");      // false (Strings are iterable but not iterators)
 * isIterator([]);          // false (Arrays are iterable but not iterators)
 * isIterator({
 *     next: function() { return true }
 * });                      // true, iterators must contain a "next" function
 * ```
 */
/*#__NO_SIDE_EFFECTS__*/
export function isIterator<T = any>(value: any): value is Iterator<T> {
    return !!value && isFunction(value.next);
}

/**
 * Checks if the value looks like it is iterable, contains a [symbol.iterator].
 *
 * @since 0.4.0
 * @group Type Identity
 * @group Iterator
 * @typeParam T - Identifies the return type of the iterator
 * @param value - The value to be checked
 * @returns True if the value is an Iterable, otherwise false
 * @example
 * ```ts
 * isIterable(null);        // false
 * isIterable(undefined);   // false
 * isIterable("null");      // true (Strings are iterable)
 * isIterable([]);          // true (Arrays are iterable)
 * ```
 */
/*#__NO_SIDE_EFFECTS__*/
export function isIterable<T = any>(value: any): value is Iterable<T> {
    return !isStrictNullOrUndefined(value) && isFunction(value[getKnownSymbol(WellKnownSymbols.iterator)]);
}

/**
 * Checks if the value looks like it is async iterable, contains a [symbol.asyncIterator].
 *
 * @since 0.14.0
 * @group Type Identity
 * @group Iterator
 * @typeParam T - Identifies the return type of the async iterator
 * @param value - The value to be checked
 * @returns True if the value is an AsyncIterable, otherwise false
 * @example
 * ```ts
 * isAsyncIterable(null);        // false
 * isAsyncIterable(undefined);   // false
 * isAsyncIterable("null");      // false
 * isAsyncIterable([]);          // false
 * ```
 */
/*#__NO_SIDE_EFFECTS__*/
export function isAsyncIterable<T = any>(value: any): value is AsyncIterable<T> {
    return !isStrictNullOrUndefined(value) && isFunction(value[getKnownSymbol(WellKnownSymbols.asyncIterator)]);
}